import { useState } from 'react';

/**
 * Custom Hook for Data Catalog State Management
 * Manages all UI state for the Data Catalog component
 */
const useDataCatalogState = () => {
  // View & Display State
  const [searchTerm, setSearchTerm] = useState('');
  const [nameView, setNameView] = useState('charite'); // 'charite', 'omop', 'both'
  const [showFilters, setShowFilters] = useState(true);
  const [expandedRows, setExpandedRows] = useState([]); // Array of expanded parameter IDs

  // Filter State
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedStations, setSelectedStations] = useState([]);
  const [selectedSensitivity, setSelectedSensitivity] = useState('all');
  const [selectedOmopStatus, setSelectedOmopStatus] = useState('all');
  const [selectedSourceSystems, setSelectedSourceSystems] = useState([]);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false); // Filter expansion panel open/closed

  // Sort State
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc'); // 'asc' or 'desc'

  // Dialog State
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [editingParameterId, setEditingParameterId] = useState(null);
  const [newTagInput, setNewTagInput] = useState('');
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [selectedParameterForList, setSelectedParameterForList] = useState(null);
  const [listManagementDialogOpen, setListManagementDialogOpen] = useState(false);
  const [setManagementDialogOpen, setSetManagementDialogOpen] = useState(false);
  const [activeSetTab, setActiveSetTab] = useState(0);

  // Tag Management State
  const [createTagPrivate, setCreateTagPrivate] = useState(true);
  const [selectedTagIcon, setSelectedTagIcon] = useState(null);
  const [deletionRequestDialog, setDeletionRequestDialog] = useState({
    open: false,
    tag: null,
    parameterId: null
  });

  // Working Lists State
  const [activeWorkingList, setActiveWorkingList] = useState([]); // Max 2 for comparison
  const [showOnlyActiveListParams, setShowOnlyActiveListParams] = useState(false);
  const [listComparisonFilter, setListComparisonFilter] = useState('all');
  const [listAddRequestDialog, setListAddRequestDialog] = useState({
    open: false,
    list: null,
    parameter: null
  });

  // AI Assistant State
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
  const [studyDescription, setStudyDescription] = useState('');
  const [studyType, setStudyType] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false);
  const [aiSuggestedParameterIds, setAiSuggestedParameterIds] = useState([]); // IDs of AI-suggested params for filtering

  // Set Parameter State
  const [setParameterIds, setSetParameterIds] = useState([]); // IDs of parameters from saved sets

  // Favorites State
  const [favorites, setFavorites] = useState([]); // Array of favorited parameter IDs

  // Snackbar State
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Row Expansion Handlers
  const handleRowClick = (parameterId) => {
    setExpandedRows(prev =>
      prev.includes(parameterId)
        ? prev.filter(id => id !== parameterId)
        : [...prev, parameterId]
    );
  };

  const isRowExpanded = (parameterId) => {
    return expandedRows.includes(parameterId);
  };

  // Filter Handlers
  const handleTagToggle = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const handleStationToggle = (station) => {
    setSelectedStations(prev =>
      prev.includes(station) ? prev.filter(s => s !== station) : [...prev, station]
    );
  };

  const handleSourceSystemToggle = (system) => {
    setSelectedSourceSystems(prev =>
      prev.includes(system) ? prev.filter(s => s !== system) : [...prev, system]
    );
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedTags([]);
    setSelectedStations([]);
    setSelectedSensitivity('all');
    setSelectedOmopStatus('all');
    setSelectedSourceSystems([]);
    setAiSuggestedParameterIds([]);
    setSetParameterIds([]);
  };

  // Favorites Handlers
  const toggleFavorite = (parameterId) => {
    setFavorites(prev =>
      prev.includes(parameterId)
        ? prev.filter(id => id !== parameterId)
        : [...prev, parameterId]
    );
  };

  return {
    // View State
    searchTerm,
    setSearchTerm,
    nameView,
    setNameView,
    showFilters,
    setShowFilters,
    expandedRows,
    setExpandedRows,

    // Filter State
    selectedTags,
    setSelectedTags,
    selectedStations,
    setSelectedStations,
    selectedSensitivity,
    setSelectedSensitivity,
    selectedOmopStatus,
    setSelectedOmopStatus,
    selectedSourceSystems,
    setSelectedSourceSystems,
    filterPanelOpen,
    setFilterPanelOpen,

    // Sort State
    orderBy,
    setOrderBy,
    order,
    setOrder,

    // Dialog State
    tagDialogOpen,
    setTagDialogOpen,
    editingParameterId,
    setEditingParameterId,
    newTagInput,
    setNewTagInput,
    listDialogOpen,
    setListDialogOpen,
    selectedParameterForList,
    setSelectedParameterForList,
    listManagementDialogOpen,
    setListManagementDialogOpen,
    setManagementDialogOpen,
    setSetManagementDialogOpen,
    activeSetTab,
    setActiveSetTab,

    // Tag Management State
    createTagPrivate,
    setCreateTagPrivate,
    selectedTagIcon,
    setSelectedTagIcon,
    deletionRequestDialog,
    setDeletionRequestDialog,

    // Working Lists State
    activeWorkingList,
    setActiveWorkingList,
    showOnlyActiveListParams,
    setShowOnlyActiveListParams,
    listComparisonFilter,
    setListComparisonFilter,
    listAddRequestDialog,
    setListAddRequestDialog,

    // AI Assistant State
    aiAssistantOpen,
    setAiAssistantOpen,
    studyDescription,
    setStudyDescription,
    studyType,
    setStudyType,
    specialty,
    setSpecialty,
    aiSuggestions,
    setAiSuggestions,
    selectedSuggestions,
    setSelectedSuggestions,
    isGeneratingSuggestions,
    setIsGeneratingSuggestions,
    aiSuggestedParameterIds,
    setAiSuggestedParameterIds,

    // Set Parameter State
    setParameterIds,
    setSetParameterIds,

    // Snackbar State
    snackbar,
    setSnackbar,

    // Favorites State
    favorites,
    setFavorites,

    // Handlers
    handleRowClick,
    isRowExpanded,
    handleTagToggle,
    handleStationToggle,
    handleSourceSystemToggle,
    clearAllFilters,
    toggleFavorite,
  };
};

export default useDataCatalogState;
